import { ArrowLeft, Zap, CheckCircle2, AlertTriangle } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Head } from "vite-react-ssg";
import { Card, CardContent } from "@/components/ui/card";
import NavigationButtons from "@/components/NavigationButtons";
import RelatedLinks from "@/components/RelatedLinks";
import { DEEPSEEK_PRICING } from "@/lib/deepseek-pricing";

const benchmarks = [
  { name: "Terminal Bench 2.1", score: "82.7", what: "Long-horizon terminal/shell agent tasks" },
  { name: "NL2Repo", score: "54.2", what: "Building a working repository from a natural-language spec" },
  { name: "Cybergym", score: "76.7", what: "Security/CTF-style agentic reasoning" },
  { name: "DeepSWE", score: "54.4", what: "Real-world software-engineering issue resolution" },
  { name: "Toolathlon (verified)", score: "70.3", what: "Multi-tool orchestration under verification" },
  { name: "Agent Last Exam", score: "25.2", what: "Hard, open-ended agent reasoning" },
  { name: "Automation Bench (Public)", score: "25.1", what: "End-to-end workflow automation" },
  { name: "DSBench-FullStack", score: "68.7", what: "Internal full-stack development suite" },
  { name: "DSBench-Hard", score: "59.6", what: "Internal hard coding-agent suite" },
];

const DeepseekV4FlashGA = () => {
  const navigate = useNavigate();
  const canonical = "https://deepseek.ai/blog/deepseek-v4-flash-ga-agent-benchmarks";

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline:
      "DeepSeek-V4-Flash Hits GA: Agent Benchmarks Beat V4-Pro-Preview, Native Responses API and Codex Support",
    datePublished: "2026-08-02T07:00:00+00:00",
    dateModified: "2026-08-02T07:00:00+00:00",
    author: {
      "@type": "Organization",
      name: "The Independent DeepSeek Guide",
      url: "https://deepseek.ai",
    },
    publisher: {
      "@type": "Organization",
      name: "The Independent DeepSeek Guide",
      logo: { "@type": "ImageObject", url: "https://deepseek.ai/og-image.png" },
    },
    description:
      "On July 31, 2026 DeepSeek shipped the official DeepSeek-V4-Flash API in public beta. Same model name, same call, post-trained for agents: Terminal Bench 2.1 82.7, DeepSWE 54.4, native Responses API and Codex support. What changes for developers, and what does not.",
    mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://deepseek.ai/" },
      { "@type": "ListItem", position: 2, name: "Blog", item: "https://deepseek.ai/blog" },
      { "@type": "ListItem", position: 3, name: "DeepSeek-V4-Flash GA", item: canonical },
    ],
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "Do I need to change my code for the official DeepSeek-V4-Flash release?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "No. DeepSeek states the API call is unchanged: keep model set to deepseek-v4-flash and you are served the latest version. There is no new model string, no new endpoint and no schema change.",
        },
      },
      {
        "@type": "Question",
        name: "Is DeepSeek-V4-Flash-0731 a new architecture?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "No. DeepSeek says the model structure and size of DeepSeek-V4-Flash-0731 are identical to DeepSeek-V4-Flash-Preview — only the post-training was redone. The gains are alignment and agent-behaviour gains, not a new base model.",
        },
      },
      {
        "@type": "Question",
        name: "How do the agent benchmarks compare to V4-Pro-Preview?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "DeepSeek reports that the official V4-Flash substantially exceeds V4-Pro-Preview on agent benchmarks: Terminal Bench 2.1 82.7, NL2Repo 54.2, Cybergym 76.7, DeepSWE 54.4, Toolathlon verified 70.3, Agent Last Exam 25.2, Automation Bench (Public) 25.1, DSBench-FullStack 68.7 and DSBench-Hard 59.6. Code-agent scores were measured with DeepSeek Harness minimal mode at the max tier, top_p 0.95, temperature 1.0.",
        },
      },
      {
        "@type": "Question",
        name: "Did DeepSeek-V4-Pro or the app change on July 31, 2026?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "No. DeepSeek explicitly notes that only the DeepSeek-V4-Flash API was upgraded. The DeepSeek-V4-Pro API and the models behind the app and web chat are unchanged. An official V4-Pro release is promised as soon as possible.",
        },
      },
      {
        "@type": "Question",
        name: "Did prices change with the V4-Flash GA release?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "No price change was announced with the July 31, 2026 update: DeepSeek-V4-Flash stayed at $0.14 per 1M input tokens on a cache miss, $0.0028 on a cache hit and $0.28 per 1M output tokens. Those rates were superseded on September 9, 2026, when DeepSeek-V4.1-Flash went generally available with a peak/off-peak rate card and V4-Flash was retired.",
        },
      },
      {
        "@type": "Question",
        name: "Does V4-Flash support the Responses API and Codex?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. The official V4-Flash natively supports the Responses API format and has been specifically adapted for Codex; DeepSeek publishes the configuration steps in its agent-integrations documentation.",
        },
      },
    ],
  };

  return (
    <>
      <Head>
        <title>DeepSeek-V4-Flash GA: Agent Scores Beat V4-Pro-Preview</title>
        <meta
          name="description"
          content="July 31, 2026: the official DeepSeek-V4-Flash API is in public beta. Same model string, agent-tuned post-training, Terminal Bench 2.1 82.7, native Responses API and Codex support."
        />
        <link rel="canonical" href={canonical} />
        <meta name="robots" content="index, follow" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonical} />
        <meta
          property="og:title"
          content="DeepSeek-V4-Flash Goes Official: Agent Benchmarks Beat V4-Pro-Preview"
        />
        <meta
          property="og:description"
          content="Same model name, same call, new post-training. Terminal Bench 2.1 82.7, DeepSWE 54.4, native Responses API and Codex adaptation — and V4-Pro is untouched."
        />
        <meta property="og:image" content="https://deepseek.ai/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Head>

      <div className="min-h-screen bg-white">
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Button
            variant="ghost"
            onClick={() => navigate("/blog")}
            className="mb-6 text-slate-600 hover:text-[#0066FF]"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Blog
          </Button>

          <nav aria-label="Breadcrumb" className="text-xs text-slate-500 mb-4">
            <Link to="/" className="hover:text-[#0066FF]">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/blog" className="hover:text-[#0066FF]">Blog</Link>
            <span className="mx-2">/</span>
            <span className="text-slate-700">DeepSeek-V4-Flash GA</span>
          </nav>

          <div className="mb-6 inline-flex items-center gap-2 text-xs font-medium bg-blue-50 text-[#0066FF] border border-blue-200 px-3 py-1.5 rounded-full">
            <Zap className="h-3.5 w-3.5" />
            API changelog · July 31, 2026 · Independent Guide
          </div>

          <article>
            <h1 className="text-3xl md:text-5xl font-bold text-slate-900 mb-4 leading-tight">
              DeepSeek-V4-Flash Is Now Official: Agent Benchmarks Beat V4-Pro-Preview, and Your Code Does Not Change
            </h1>

            <p className="text-lg text-slate-600 mb-2">
              By the Deep Seek AI editorial desk · August 2, 2026 · 7 min read
            </p>
            <p className="text-base text-slate-500 mb-8 italic">
              DeepSeek pushed the official <code>deepseek-v4-flash</code> API into public beta on
              July 31, 2026. Same model string, same endpoint, same architecture — but a fresh
              post-training pass that lifts agent scores clean past V4-Pro-Preview. Here is what
              actually changed, what did not, and what it means for the cheap tier of the DeepSeek API.
            </p>

            <Card className="mb-8 border-blue-200 bg-blue-50/60">
              <CardContent className="p-6">
                <h2 className="text-lg font-bold text-slate-900 mb-3 mt-0">The 60-second version</h2>
                <ul className="text-sm text-slate-800 space-y-2 list-disc pl-5">
                  <li>
                    The <strong>official (正式版) DeepSeek-V4-Flash API</strong> is live in public
                    beta. Keep calling <code>deepseek-v4-flash</code> — you get the newest version
                    automatically.
                  </li>
                  <li>
                    <strong>Agent capability is the headline.</strong> DeepSeek reports the official
                    V4-Flash substantially exceeding V4-Pro-Preview across nine agent benchmarks.
                  </li>
                  <li>
                    <strong>Same model, new post-training.</strong> V4-Flash-0731 keeps the exact
                    structure and size of V4-Flash-Preview; only post-training was redone.
                  </li>
                  <li>
                    <strong>Native Responses API + Codex.</strong> The official Flash speaks the
                    Responses API format natively and is specifically adapted for Codex.
                  </li>
                  <li>
                    <strong>V4-Pro and the app are untouched.</strong> Only the Flash API was
                    upgraded. An official V4-Pro release is promised "as soon as possible".
                  </li>
                </ul>
              </CardContent>
            </Card>

            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-10 mb-4">
              The agent benchmarks DeepSeek published
            </h2>
            <p className="text-slate-700 mb-4">
              All figures below are DeepSeek's own reported numbers from the July 31, 2026 changelog
              entry. For public-benchmark code-agent tasks, DeepSeek says the official V4-Flash was
              evaluated using <strong>DeepSeek Harness minimal mode</strong> (announced as
              forthcoming) at the <strong>max tier</strong>, with <code>top_p = 0.95</code> and{" "}
              <code>temperature = 1.0</code>. Those settings matter: agent scores are extremely
              sensitive to the harness, so treat them as vendor-reported until independently
              reproduced.
            </p>

            <div className="overflow-x-auto mb-6">
              <table className="w-full text-sm border border-slate-200 rounded-lg overflow-hidden">
                <caption className="sr-only">
                  DeepSeek-V4-Flash official release agent benchmark scores, July 31 2026
                </caption>
                <thead className="bg-slate-100">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold text-slate-700">Benchmark</th>
                    <th className="text-right px-4 py-3 font-semibold text-slate-700">V4-Flash (official)</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-700">What it measures</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {benchmarks.map((b) => (
                    <tr key={b.name}>
                      <td className="px-4 py-3 font-medium text-slate-900">{b.name}</td>
                      <td className="text-right px-4 py-3 font-mono">{b.score}</td>
                      <td className="px-4 py-3 text-slate-600">{b.what}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-slate-700 mb-8">
              Two of those suites are internal: <strong>DSBench-FullStack</strong> is DeepSeek's
              in-house full-stack development set and <strong>DSBench-Hard</strong> its hard
              coding-agent set. They are not third-party comparable — useful as a signal of intent,
              not as a leaderboard.
            </p>

            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-10 mb-4">
              Why "same structure, new post-training" is the most important line
            </h2>
            <p className="text-slate-700 mb-4">
              DeepSeek states plainly that <code>DeepSeek-V4-Flash-0731</code> has the same model
              structure and size as <code>DeepSeek-V4-Flash-Preview</code>, and that only the
              post-training was rerun. That has three practical consequences:
            </p>
            <ul className="text-slate-700 space-y-3 list-disc pl-6 mb-8">
              <li>
                <strong>Latency and cost profiles should be stable.</strong> No parameter-count
                change means no reason to expect a different throughput or price tier, and none was
                announced.
              </li>
              <li>
                <strong>Your prompts may still shift behaviour.</strong> Post-training is exactly
                what changes tool-calling style, refusal behaviour and verbosity. If you have
                prompt-brittle agent scaffolding, re-run your evals this week.
              </li>
              <li>
                <strong>Self-hosters are unaffected for now.</strong> This was an API-side upgrade;
                the changelog does not announce new open weights.
              </li>
            </ul>

            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-10 mb-4">
              Native Responses API and Codex adaptation
            </h2>
            <p className="text-slate-700 mb-4">
              The official V4-Flash natively supports the <strong>Responses API format</strong> and
              has been specifically adapted for <strong>Codex</strong>. Practically, that means you
              can point a Codex-style agent harness at DeepSeek without a translation shim between
              chat-completions and Responses-style items. DeepSeek documents the configuration in its{" "}
              <a
                href="https://api-docs.deepseek.com/zh-cn/quick_start/agent_integrations/codex"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#0066FF] underline"
              >
                agent integrations guide
              </a>
              .
            </p>
            <p className="text-slate-700 mb-8">
              Combined with the agent benchmarks, the positioning is hard to miss: DeepSeek wants
              Flash — the cheap tier — to be the default model behind coding agents, not the
              fallback you use when Pro is too expensive.
            </p>

            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-10 mb-4">
              What this costs you
            </h2>
            <p className="text-slate-700 mb-4">
              No pricing change accompanied the July 31 update. The rates in force at the time, per
              1,000,000 tokens in USD (these were replaced at the V4.1-Flash GA release on September
              9, 2026 — see our{" "}
              <Link to="/pricing" className="text-[#0066FF] underline">current pricing page</Link>):
            </p>
            <div className="overflow-x-auto mb-4">
              <table className="w-full text-sm border border-slate-200 rounded-lg overflow-hidden">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold text-slate-700">Model</th>
                    <th className="text-right px-4 py-3 font-semibold text-slate-700">Input (cache miss)</th>
                    <th className="text-right px-4 py-3 font-semibold text-slate-700">Input (cache hit)</th>
                    <th className="text-right px-4 py-3 font-semibold text-slate-700">Output</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  <tr>
                    <td className="px-4 py-3 font-medium">DeepSeek-V4-Flash</td>
                    <td className="text-right px-4 py-3">$0.14</td>
                    <td className="text-right px-4 py-3">$0.0028</td>
                    <td className="text-right px-4 py-3">$0.28</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">DeepSeek-V4-Pro</td>
                    <td className="text-right px-4 py-3">$0.435</td>
                    <td className="text-right px-4 py-3">$0.003625</td>
                    <td className="text-right px-4 py-3">$0.87</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-slate-700 mb-8">
              At the time no peak-hour surcharge was active; one went live with the V4.1-Flash GA
              release on September 9, 2026. We re-check the official rate card weekly and publish every change
              on our{" "}
              <Link to="/pricing" className="text-[#0066FF] underline">
                DeepSeek pricing page
              </Link>
              .
            </p>

            <Card className="mb-8 border-amber-200 bg-amber-50/60">
              <CardContent className="p-6">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h2 className="text-lg font-bold text-amber-900 mb-2 mt-0">
                      What did <em>not</em> change
                    </h2>
                    <p className="text-sm text-amber-900">
                      DeepSeek is explicit: only the DeepSeek-V4-Flash API was upgraded. The{" "}
                      <strong>DeepSeek-V4-Pro API</strong> and the models serving the{" "}
                      <strong>app and web chat</strong> are unchanged. If you benchmarked Pro last
                      week, those numbers still stand — and the official V4-Pro release is still
                      pending, promised "as soon as possible".
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-10 mb-4">
              Your migration checklist (it is short)
            </h2>
            <ul className="text-slate-700 space-y-3 mb-8">
              {[
                "Keep model: \"deepseek-v4-flash\" — no string change, no endpoint change.",
                "Re-run your agent and tool-calling evals: post-training changed, so behaviour can shift.",
                "If you run a Codex-style harness, try the native Responses API path and drop your shim.",
                "Do not switch to V4-Pro for agent work on price grounds alone — Flash now leads on the published agent suites.",
                "Re-check the official rate card before you commit budget; no price change was announced with this release.",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-10 mb-4">
              Frequently asked questions
            </h2>
            <div className="space-y-5 mb-10">
              {faqSchema.mainEntity.map((q: any) => (
                <div key={q.name} className="border-b border-slate-200 pb-4">
                  <h3 className="font-semibold text-slate-900 mb-2">{q.name}</h3>
                  <p className="text-slate-700 text-sm">{q.acceptedAnswer.text}</p>
                </div>
              ))}
            </div>

            <p className="text-sm text-slate-500 mb-10">
              Source: DeepSeek's official API changelog entry dated 2026-07-31 (
              <a
                href="https://api-docs.deepseek.com/zh-cn/updates/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#0066FF] underline"
              >
                api-docs.deepseek.com/updates
              </a>
              ). This is an independent fan-run guide and is not affiliated with DeepSeek.
            </p>

            <div className="border-t border-slate-200 pt-8">
              <h2 className="text-2xl font-bold mb-6">Related reading</h2>
              <RelatedLinks />
            </div>
          </article>

          <NavigationButtons />
        </main>
      </div>
    </>
  );
};

export default DeepseekV4FlashGA;
